import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  COMPANY_TIMEZONE_GROUPS,
  formatTimezoneLabel,
  isKnownCompanyTimezone,
  timezoneKeywords,
} from "@/lib/timezones";
import { cn } from "@/lib/utils";
import { Globe } from "lucide-react";
import { useState } from "react";

type Props = {
  label?: string;
  value: string;
  onChange: (next: string) => void;
};

export function TimezoneSelect({ label = "Timezone", value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [openCycle, setOpenCycle] = useState(0);
  const trimmed = value.trim();
  const inList = isKnownCompanyTimezone(trimmed);
  const display = trimmed ? formatTimezoneLabel(trimmed) : "Select timezone…";

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Popover
        modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) setOpenCycle((c) => c + 1);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "glass-input h-10 w-full justify-start text-left font-normal text-sm",
              !trimmed && "text-muted-foreground",
            )}
          >
            <Globe className="mr-2 h-4 w-4 opacity-70 shrink-0" />
            <span className="truncate">{display}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(100vw-2rem,22rem)] p-0 z-[100]" align="start" sideOffset={6}>
          <Command key={openCycle} shouldFilter>
            <CommandInput placeholder="Search timezone (e.g. Karachi, Dubai, New York)…" className="h-11" />
            <CommandList>
              <CommandEmpty>No matching timezone.</CommandEmpty>
              <CommandGroup heading="Actions">
                <CommandItem
                  value="__timezone_clear__"
                  keywords={["clear", "none", "empty"]}
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                >
                  Clear selection
                </CommandItem>
              </CommandGroup>
              {trimmed && !inList ? (
                <CommandGroup heading="Current (not in list)">
                  <CommandItem
                    value={trimmed}
                    keywords={[trimmed]}
                    onSelect={() => {
                      onChange(trimmed);
                      setOpen(false);
                    }}
                    className="bg-accent/80"
                  >
                    {formatTimezoneLabel(trimmed)}{" "}
                    <span className="text-muted-foreground">(not in list)</span>
                  </CommandItem>
                </CommandGroup>
              ) : null}
              {COMPANY_TIMEZONE_GROUPS.map((group) => (
                <CommandGroup key={group.region} heading={group.region}>
                  {group.zones.map((tz) => (
                    <CommandItem
                      key={tz}
                      value={tz}
                      keywords={timezoneKeywords(tz, group.region)}
                      onSelect={() => {
                        onChange(tz);
                        setOpen(false);
                      }}
                      className={cn(tz === trimmed && "bg-accent/80")}
                    >
                      {formatTimezoneLabel(tz)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
